import React, { useCallback, useEffect, useState } from "react";
import { Input, Select, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import supplierService from "../../../services/supplierService";
import ReportShell from "../ReportShell";
import { CATEGORY_OPTIONS } from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { Text } = Typography;

export type StockReportMode = "all" | "near_expiry" | "expired" | "low_stock";

const MODE_CONFIG: Record<
  StockReportMode,
  { title: string; expiry?: string; stockStatus?: string }
> = {
  all: { title: "Stock Report" },
  near_expiry: { title: "Near Expiry", expiry: "near" },
  expired: { title: "Expired Stock", expiry: "expired" },
  low_stock: { title: "Low Stock", stockStatus: "low" },
};

interface Props {
  pharmacy: PharmacyHeader;
  mode: StockReportMode;
}

const StockReport: React.FC<Props> = ({ pharmacy, mode }) => {
  const config = MODE_CONFIG[mode];

  const [category, setCategory] = useState<string | undefined>();
  const [supplier, setSupplier] = useState<string | undefined>();
  const [stockStatus, setStockStatus] = useState<string | undefined>();
  const [expiryDays, setExpiryDays] = useState<number>(30);
  const [search, setSearch] = useState("");
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supplierService
      .getSuppliers({ limit: 200 })
      .then((res: any) => setSuppliers(res?.data?.suppliers || res?.data || []))
      .catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const params: Record<string, any> = {
      category,
      supplier,
      search: search || undefined,
      stockStatus: config.stockStatus || stockStatus,
    };
    if (config.expiry === "near") {
      params.expiry = "near";
      params.expiryDays = expiryDays;
      params.sortBy = "expiry";
    } else if (config.expiry === "expired") {
      params.expiry = "expired";
      params.sortBy = "expiry";
    }
    return params;
  }, [category, supplier, search, stockStatus, expiryDays, config]);

  const load = useCallback(
    async (p = page, ps = pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.getInventoryReport({
          ...buildParams(),
          page: p,
          limit: ps,
        });
        setData(res.data.data);
      } catch (e: any) {
        setError(e.response?.data?.message || "Failed to load stock report");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, page, pageSize]
  );

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, supplier, stockStatus, expiryDays, mode]);

  const summary = data?.summary || {};

  const exportColumns: ExportColumn[] = [
    { title: "Product", value: (r) => r.name || "" },
    { title: "Category", value: (r) => r.category || "" },
    { title: "Manufacturer", value: (r) => r.manufacturer || "" },
    { title: "Supplier", value: (r) => r.supplier?.name || "" },
    { title: "Batch", value: (r) => r.batchNumber || "" },
    {
      title: "Expiry",
      value: (r) => (r.expiryDate ? dayjs(r.expiryDate).format("DD/MM/YYYY") : ""),
    },
    { title: "Qty", value: (r) => r.quantity?.current ?? 0 },
    { title: "Min Qty", value: (r) => r.quantity?.minimum ?? 0 },
    { title: "Unit", value: (r) => r.quantity?.unit || "" },
    { title: "Purchase Rate", value: (r) => (r.pricing?.purchasePrice || 0).toFixed(2) },
    { title: "MRP", value: (r) => (r.pricing?.mrp || 0).toFixed(2) },
    {
      title: "Stock Value (cost)",
      value: (r) =>
        ((r.quantity?.current || 0) * (r.pricing?.purchasePrice || 0)).toFixed(2),
    },
    { title: "GST %", value: (r) => r.gstRate ?? r.pricing?.taxPercent ?? 0 },
  ];

  const fetchAll = async () => {
    const res = await adminApi.getInventoryReport({
      ...buildParams(),
      format: "all",
    });
    return res.data.data;
  };

  const handleExportCsv = async () => {
    const full = await fetchAll();
    const s = full.summary || {};
    exportCsv(config.title, exportColumns, full.rows || [], [
      `As on: ${dayjs().format("DD/MM/YYYY HH:mm")}`,
      `Batches: ${s.batchCount || 0} | Total qty: ${s.totalQty || 0} | Cost value: ${(s.purchaseValue || 0).toFixed(2)} | MRP value: ${(s.mrpValue || 0).toFixed(2)}`,
    ]);
  };

  const handleExportPdf = async () => {
    const full = await fetchAll();
    const s = full.summary || {};
    exportPdf({
      title: config.title,
      pharmacy,
      subtitle: `As on: ${dayjs().format("DD/MM/YYYY HH:mm")}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: full.rows || [],
      summary: [
        { label: "Batches", value: String(s.batchCount || 0) },
        { label: "Total qty", value: String(s.totalQty || 0) },
        { label: "Cost value", value: pdfCurrency(s.purchaseValue) },
        { label: "MRP value", value: pdfCurrency(s.mrpValue) },
      ],
    });
  };

  const reset = () => {
    setCategory(undefined);
    setSupplier(undefined);
    setStockStatus(undefined);
    setExpiryDays(30);
    setSearch("");
  };

  const expiryTag = (d: string) => {
    if (!d) return "—";
    const exp = dayjs(d);
    const days = exp.diff(dayjs(), "day");
    const label = exp.format("DD/MM/YYYY");
    if (days < 0) return <Tag color="red">{label}</Tag>;
    if (days <= 30) return <Tag color="orange">{label}</Tag>;
    if (days <= 90) return <Tag color="gold">{label}</Tag>;
    return label;
  };

  return (
    <ReportShell
      title={config.title}
      loading={loading}
      error={error}
      onRefresh={() => load()}
      onReset={reset}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <Select
            size="small"
            allowClear
            placeholder="Category"
            style={{ width: 130 }}
            value={category}
            onChange={setCategory}
            options={CATEGORY_OPTIONS}
          />
          <Select
            size="small"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Supplier"
            style={{ width: 170 }}
            value={supplier}
            onChange={setSupplier}
            options={suppliers.map((s: any) => ({ value: s._id, label: s.name }))}
          />
          {mode === "all" && (
            <Select
              size="small"
              allowClear
              placeholder="Stock status"
              style={{ width: 130 }}
              value={stockStatus}
              onChange={setStockStatus}
              options={[
                { value: "in_stock", label: "In stock" },
                { value: "low", label: "Low stock" },
                { value: "out", label: "Out of stock" },
              ]}
            />
          )}
          {mode === "near_expiry" && (
            <Select
              size="small"
              style={{ width: 140 }}
              value={expiryDays}
              onChange={setExpiryDays}
              options={[
                { value: 30, label: "Within 30 days" },
                { value: 60, label: "Within 60 days" },
                { value: 90, label: "Within 90 days" },
                { value: 180, label: "Within 180 days" },
              ]}
            />
          )}
          <Input.Search
            size="small"
            placeholder="Name / batch"
            style={{ width: 170 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1);
              load(1, pageSize);
            }}
            allowClear
          />
        </>
      }
      stats={[
        { title: "Batches", value: summary.batchCount || 0 },
        { title: "Total qty", value: summary.totalQty || 0 },
        {
          title: "Stock value (cost)",
          value: summary.purchaseValue || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Stock value (MRP)",
          value: summary.mrpValue || 0,
          prefix: "₹",
          precision: 2,
        },
      ]}
    >
      <Table
        rowKey="_id"
        size="small"
        sticky
        loading={loading}
        dataSource={data?.rows || []}
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize,
          total: data?.pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} batches`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
        columns={[
          { title: "Product", dataIndex: "name", ellipsis: true, fixed: "left" },
          { title: "Category", dataIndex: "category", width: 90 },
          { title: "Batch", dataIndex: "batchNumber", width: 100 },
          {
            title: "Expiry",
            dataIndex: "expiryDate",
            width: 120,
            render: expiryTag,
          },
          {
            title: "Qty",
            width: 80,
            align: "right" as const,
            render: (_: unknown, r: any) => {
              const cur = r.quantity?.current ?? 0;
              const min = r.quantity?.minimum ?? 0;
              return cur === 0 ? (
                <Text type="danger">{cur}</Text>
              ) : cur <= min ? (
                <Text type="warning">{cur}</Text>
              ) : (
                cur
              );
            },
          },
          {
            title: "Min",
            width: 60,
            align: "right" as const,
            render: (_: unknown, r: any) => r.quantity?.minimum ?? 0,
          },
          {
            title: "Pur. Rate",
            width: 100,
            align: "right" as const,
            render: (_: unknown, r: any) => formatCurrency(r.pricing?.purchasePrice),
          },
          {
            title: "MRP",
            width: 100,
            align: "right" as const,
            render: (_: unknown, r: any) => formatCurrency(r.pricing?.mrp),
          },
          {
            title: "Value (cost)",
            width: 110,
            align: "right" as const,
            render: (_: unknown, r: any) =>
              formatCurrency(
                (r.quantity?.current || 0) * (r.pricing?.purchasePrice || 0)
              ),
          },
          {
            title: "Supplier",
            width: 140,
            ellipsis: true,
            render: (_: unknown, r: any) => r.supplier?.name || "—",
          },
        ]}
      />
    </ReportShell>
  );
};

export default StockReport;
